import { GridSelectionModel, useGridApiRef } from '@mui/x-data-grid-pro';
import { Form, Formik } from 'formik';
import { useCallback, useEffect, useMemo, useState } from 'react';
import useSWR, { useSWRConfig } from 'swr';
import { v4 } from 'uuid';
import { boolean, object, string } from 'yup';
import { useDispatch } from 'react-redux';

import { IAsset } from '@modules/ContactManager/components/Tabs/components/Assets/interfaces';
import {
  IConsentPreferenceTypeItem,
  IContactDetails,
} from '@modules/ContactManager/components/Tabs/components/Contact/components/OptOutModal/interfaces';
import { getAssetOptions } from '@modules/ContactManager/components/Tabs/components/Assets/utils';
import { NContact } from '@modules/ContactManager/components/Tabs/components/Contact/validation';
import { IContactData, IDeal } from '@modules/ContactManager/components/Tabs/components/Deals/interfaces';
import { getOptions, withQueryParams } from '@shared/commonFunctions';
import { Notistack } from '@shared/components/Notistack/Notistack';
import { navigateToLastGridRow } from '@shared/components/grid/GridColActions';
import { Modal } from '@shared/components/modals/Modal';
import { SERVICES } from '@shared/constants/apiConfig';
import { Loader } from '@shared/enhancers/withLoader';
import { CancelButton, SaveButton } from '@shared/inputs/Buttons';
import { EStipTypeEntity, IStipType } from '@shared/interfaces';
import { CommonButtonWrapper } from '@styles/CommonStyles';
import { ModalWrapper } from '@styles/StyledDiv';
import { setRefreshFiles } from '@store/reducers/contactManagerSlice';
import { useGetConsentQuery } from '@modules/ContactManager/components/Tabs/components/Contact/queries';
import { ILead } from '@modules/ContactManager/components/Tabs/components/Leads/types/interfaces/interfaces';
import { IStipsRequest, IStipsRequestListItem } from '../../interfaces';
import { sendStipRequest } from '../../requests';
import { LeadNoAssetAssignedDataModal } from '../LeadNoAssetAssignedModal';
import { Header } from './components/Header';
import { StipRequestForm } from './components/StipRequestForm';
import { StipsTable } from './components/StipsTable';


const getMappedContactConsentOptOutData = (optOutItems: IConsentPreferenceTypeItem[]): IContactOptOutData => {
  const result: IContactOptOutData = optOutItems.reduce(
    (acc, cur) => {
      if (cur.consentPreferenceName === 'Application_Email') {
        acc = {
          ...acc,
          checkContactEmail: cur.isOptedIn,
        };
      }

      if (cur.consentPreferenceName === 'Application_SMS') {
        acc = {
          ...acc,
          checkContactText: cur.isOptedIn,
        };
      }

      return acc;
    },
    {
      checkContactEmail: false,
      checkContactText: false,
    } as IContactOptOutData
  );

  return result;
};

const validationSchema = object().shape({
  checkContactEmail: boolean(),
  checkContactText: boolean(),
  checkCoContactEmail: boolean(),
  checkCoContactText: boolean(),
  contactPhone: string().phone().notRequired().when('checkContactText', {
    is: true,
    then: string().phone().required(),
  }),
  contactEmail: string().email().notRequired().when('checkContactEmail', {
    is: true,
    then: string().email().required(),
  }),
  coBorrowerPhone: string().phone().notRequired().when('checkCoContactText', {
    is: true,
    then: string().phone().required(),
  }),
  coBorrowerEmail: string().email().notRequired().when('checkCoContactEmail', {
    is: true,
    then: string().email().required(),
  }),
});

interface IStipRequestModalProps {
  stipModalPosition: number;
  handleClose: () => void;
  assetId: string;
  assetName: string;
  contactId: string;
  dealId?: string;
  leadId?: string;
  leads: ILead[];
  dealNumericId?: number;
  assetsData: IAsset[];
}

export interface IStipRequestForm extends Omit<IStipsRequest, 'stipsRequestItems' | 'contactId' | 'assetId'> {
  note: string;
  checkContactEmail: boolean;
  checkContactText: boolean;
  checkCoContactEmail: boolean;
  checkCoContactText: boolean;
}

interface RowData extends IStipsRequestListItem {
  isNew: boolean;
  showDeleteButton: boolean;
}

interface IContactOptOutData {
  checkContactEmail: boolean;
  checkContactText: boolean;
}

export interface IAssetOption {
  id: string;
  display: string;
}

export const NEW_STIP = 'newStip';

export const StipRequestModal = ({
  stipModalPosition,
  handleClose,
  contactId,
  assetId,
  assetName,
  assetsData,
  dealId,
  leadId,
  leads,
  dealNumericId,
}: IStipRequestModalProps) => {
  const tableRef = useGridApiRef();
  const dispatch = useDispatch();
  const assetOptions = useMemo(() => getAssetOptions(assetsData), [assetsData]);
  const defaultselectedAsset: IAssetOption = useMemo(
    () =>
      (assetOptions || []).length > 1
        ? { id: assetId, display: assetName }
        : { id: assetOptions[0]?.id, display: assetOptions[0]?.display },
    [assetId, assetName, assetOptions]
  );
  const [selectedAsset, setAsset] = useState<IAssetOption | null>(defaultselectedAsset);
  const [selectedStipType, setSelectedStipType] = useState<IStipType | null>(null);
  const [selectedContactId, setContactId] = useState<string | null>(null);
  const [isSending, setSendingStatus] = useState(false);
  const [editingRowId, setEditingRowId] = useState<string | null>(null);
  const [selectedRowIds, setSelectedRowIds] = useState<GridSelectionModel>([]);

  const [IsAssetRequiredModel, setIsAssetRequiredModel] = useState(0);
  const { data: stipTypes } = useSWR<IStipType[]>(
    withQueryParams(`${SERVICES.DOCUMENT}/stips/request/stiptypes`, { leadId }, false),
    { revalidateOnMount: undefined }
  );
  const { data: contactData } = useSWR<NContact.Entity>(contactId && `/${SERVICES.CONTACT}/contacts/${contactId}`, {
    revalidateOnMount: true,
  });

  const coBorrower = contactData?.coApplicants?.find(
    (coApplicant) => !coApplicant.isDroppedContact
  );
  
  const coBorrowerId = coBorrower?.id;
  const { mutate } = useSWRConfig();

  const stipReviewResponseQueryPath = withQueryParams(
    `${SERVICES.DOCUMENT}/stips/request`,
    { contactId, assetId, leadId },
    false
  );
  const {
    data: stipReviewResponse,
    isValidating: isValidatingStipReview,
    error: stipReviewError,
    mutate: mutateStipReview,
  } = useSWR<IStipsRequest>(stipReviewResponseQueryPath, {
    revalidateOnMount: true,
  });

  const { data: stipContacts } = useSWR<IContactData>(
    contactId && selectedStipType?.relatedToEntity === EStipTypeEntity.CONTACT && `${SERVICES.CONTACT}/contacts/${contactId}`,
    { revalidateOnMount: true }
  );

  const { data: stipDeals } = useSWR<IDeal[]>(
    contactId && selectedStipType?.relatedToEntity === EStipTypeEntity.DEAL && `/${SERVICES.DEAL}/contacts/${contactId}/deals`,
    { revalidateOnMount: true }
  );

  const contactDetails: IContactDetails = {
    firstName: contactData?.firstName ?? '',
    lastName: contactData?.lastName ?? '',
    email: contactData?.email ?? '',
    cellPhone: contactData?.cellPhone ?? '',
  };

  const coContactDetails: IContactDetails = {
    firstName: coBorrower?.firstName ?? '',
    lastName: coBorrower?.lastName ?? '',
    email: coBorrower?.email ?? '',
    cellPhone: coBorrower?.cellPhone ?? '',
  };

  //Call consent APIs
  const {
    consentData: borrowerConsentData,
    isValidating: isValidatingBorrower,
    error: borrowerError,
  } = useGetConsentQuery(contactDetails);
  const {
    consentData: coBorrowerConsentData,
    isValidating: isValidatingCoBorrower,
    error: coBorrowerError,
  } = useGetConsentQuery(coContactDetails);

  useEffect(
    () => () => {
      // Clean cache for Stips for getting latest data on open modal
      mutate(stipReviewResponseQueryPath, undefined, false);
    },
    []
  );

  const isLoading = useMemo(() => {
    const isStipsRequestLoading = (!stipReviewResponse && !stipReviewError) || isValidatingStipReview;
    const isBorrowerOptOutDataLoading = (!!contactId && !borrowerConsentData && !borrowerError) || isValidatingBorrower;
    const isCoBorrowerOptOutDataLoading =
      (!!coBorrowerId && !coBorrowerConsentData && !coBorrowerError) || isValidatingCoBorrower;
    return isStipsRequestLoading || isBorrowerOptOutDataLoading || isCoBorrowerOptOutDataLoading;
  }, [
    stipReviewResponse,
    stipReviewError,
    isValidatingStipReview,
    borrowerConsentData,
    borrowerError,
    isValidatingBorrower,
    coBorrowerConsentData,
    coBorrowerError,
    isValidatingCoBorrower,
    contactId,
    coBorrowerId,
  ]);

  const stipTypeOptions = useMemo(() => getOptions(stipTypes), [stipTypes]);

  const contactsOptions = useMemo(() => {
    let options: { value: string; label: string }[] = [];

    if (selectedStipType?.relatedToEntity === EStipTypeEntity.CONTACT && stipContacts) {
      const coApplicants = stipContacts.coApplicants.map((item) => ({
        value: item.id,
        label: `${item.firstName} ${item.lastName}`,
      }));

      options = [
        {
          value: stipContacts?.id ?? '',
          label: `${stipContacts?.firstName ?? ''} ${stipContacts?.lastName ?? ''}`,
        },
        ...coApplicants,
      ];
    }

    return options;
  }, [selectedStipType, stipContacts, stipDeals]);

  const tableActions = useMemo(
    () => ({
      onSave: () => {
        setEditingRowId(null);
      },
      onCancel: () => {
        setEditingRowId(null);
      },
    }),
    [contactId]
  );

  const initialValues: IStipRequestForm = useMemo(() => {
    const contactOptOut = getMappedContactConsentOptOutData(borrowerConsentData ?? []);
    const coBorrowerOptOut = getMappedContactConsentOptOutData(coBorrowerConsentData ?? []);

    return {
      contactEmail: stipReviewResponse?.contactEmail ?? '',
      contactPhone: stipReviewResponse?.contactPhone ?? '',
      coBorrowerId: stipReviewResponse?.coBorrowerId ?? null,
      coBorrowerEmail: stipReviewResponse?.coBorrowerEmail ?? '',
      coBorrowerPhone: stipReviewResponse?.coBorrowerPhone ?? '',
      note: stipReviewResponse?.note ?? '',
      checkContactEmail: contactOptOut.checkContactEmail,
      checkContactText: contactOptOut.checkContactText,
      checkCoContactEmail: coBorrowerOptOut.checkContactEmail,
      checkCoContactText: coBorrowerOptOut.checkContactText,
    };
  }, [stipReviewResponse, borrowerConsentData, coBorrowerConsentData]);

  useMemo(() => {
    if (!isLoading) {
      const defaultSelectedIds = (stipReviewResponse?.stipsRequestItems ?? []).map((stipReview) => stipReview.id);
      setSelectedRowIds(defaultSelectedIds);
    }
  }, [isLoading]);

  const handleSubmit = useCallback(
    (formData: IStipRequestForm) => {
      if (
        formData.checkContactEmail ||
        formData.checkContactText ||
        formData.checkCoContactEmail ||
        formData.checkCoContactText
      ) {
        setSendingStatus(true);

        const stipRequestItems = Array.from(tableRef.current.getSelectedRows().values()).map((row) => ({
          contactId: row.contactId,
          additionalNote: row?.additionalNote ?? '',
          dealId: row.dealId,
          assetId: row.assetId,
          stipTypeId: row.stipTypeId,
          coBorrowerId: row.coBorrowerId,
          description: row.description,
          id: row.id,
          isCoBorrower: row.isCoBorrower,
          // stipTypeName: row.stipTypeName ?? '',
          // relatedToEntity: row.relatedToEntity,
        }));

        sendStipRequest({
          contactId: contactId || '',
          note: formData.note,
          contactEmail: formData.checkContactEmail ? formData.contactEmail : null,
          contactPhone: formData.checkContactText ? formData.contactPhone : null,
          coBorrowerEmail: formData.checkCoContactEmail ? formData.coBorrowerEmail : null,
          coBorrowerPhone: formData?.checkCoContactText ? formData.coBorrowerPhone : null,
          coBorrowerId: formData.coBorrowerId,
          stipRequestItems,
          assetId,
          contactSendEmail: formData.checkContactEmail,
          contactSendText: formData.checkContactText,
          coBorrowerSendEmail: formData.checkCoContactEmail,
          coBorrowerSendText: formData.checkCoContactText,
        }).then(() => {
          mutateStipReview().then(() => {
            setSendingStatus(false);
            dispatch(setRefreshFiles(new Date().toISOString()));
          });
        });
      } else {
        Notistack.generalError('Please select a method for communication');
      }
    },
    [contactId, assetId]
  );

  const handleValidateAssetSelect = useCallback(
    (assetId: string) => {
      const leadsWithAsset = leads.filter((lead) => lead?.assetId)?.map((lead) => lead?.assetId);
      if (!leadsWithAsset?.includes(assetId)) {
        setIsAssetRequiredModel(1);
      }
    },
    [leads]
  );

  const handleAddStip = useCallback(() => {
    if (selectedStipType) {
      const existingStipTypes = Array.from(tableRef.current.getRowModels().values()).map(
        (row) => `${row.stipTypeId}-${row.contactId ?? row.assetId}`
      );

      if (
        existingStipTypes.includes(`${selectedStipType.id}-${selectedContactId}`) ||
        existingStipTypes.includes(`${selectedStipType.id}-${selectedAsset?.id}`)
      ) {
        const contactName = contactsOptions.find((contact) => contact.value === selectedContactId)?.value ?? '';
        Notistack.generalError(`There is already a '${selectedStipType.name}' ${contactName && `for ${contactName}`}.
          To re-request an item click on the 'Request' checkbox on the left side of the grid and then send the request.`);
        return;
      }

      // Generate ID only for Grid Table component functionality
      const id = v4() as string;

      const updateRowData: RowData = {
        id,
        stipTypeId: selectedStipType.id || null,
        stipTypeName: selectedStipType.name,
        relatedToEntity: selectedStipType?.relatedToEntity,
        assetId: null,
        contactId: null,
        dealId: null,
        isNew: true,
        showDeleteButton: true,
        additionalNote: '',
        description: '',
        review: '',
        receivedDate: NEW_STIP,
        requestedDate: '',
      };

      if (selectedStipType?.relatedToEntity === EStipTypeEntity.CONTACT) {
        updateRowData.contactId = selectedContactId;
        updateRowData.isCoBorrower = stipContacts?.id !== selectedContactId;

        updateRowData.description = contactsOptions.find((option) => option.value === selectedContactId)?.label ?? '';
      }

      if (selectedStipType?.relatedToEntity === EStipTypeEntity.DEAL) {
        updateRowData.dealId = dealId ?? null;
        updateRowData.description = String(dealNumericId) ?? '';
      }

      if (selectedStipType?.relatedToEntity === EStipTypeEntity.ASSET) {
        updateRowData.assetId = selectedAsset?.id || '';

        if (selectedAsset?.display) {
          updateRowData.description = selectedAsset?.display || '';
        }
      }

      tableRef.current.updateRows([{ ...updateRowData }]);

      setTimeout(() => {
        setSelectedRowIds((prevState) => [...prevState, id]);
        navigateToLastGridRow(tableRef.current, (stipReviewResponse?.stipsRequestItems?.length ?? 0) + 1);
      });

      setEditingRowId(id);
    }
  }, [
    selectedContactId,
    selectedStipType,
    contactsOptions,
    stipContacts,
    editingRowId,
    stipReviewResponse?.stipsRequestItems,
    dealId,
    selectedAsset?.id,
    selectedAsset?.display,
    dealNumericId,
  ]);

  const handleSelectStipType = useCallback(
    (stipTypeId: string) => {
      const stipType = stipTypes?.find((stipType) => stipType.id === stipTypeId);

      setSelectedStipType(stipType || null);
      setContactId(null);
    },
    [stipTypes]
  );

  return (
    <Modal
      fullWidth
      hideBackdrop
      isLoading={isLoading}
      title="STIPS Request"
      resetPosition={stipModalPosition}
      open={!!stipModalPosition}
      onClose={handleClose}
    >
      {isLoading ? (
        <Loader />
      ) : (
        <ModalWrapper style={{ minHeight: '500px' }}>
          <Header
            selectedStipType={selectedStipType}
            stipTypeOptions={stipTypeOptions}
            selectStipType={handleSelectStipType}
            assetOptions={assetOptions || []}
            selectedContact={selectedContactId}
            contactsOptions={contactsOptions}
            selectedAsset={selectedAsset}
            selectContact={setContactId}
            selectAsset={setAsset}
            handleAddStip={handleAddStip}
            handleValidateAssetSelect={handleValidateAssetSelect}
          />

          <StipsTable
            apiRef={tableRef}
            rows={stipReviewResponse?.stipsRequestItems ?? []}
            editingRowId={editingRowId}
            selectedRowIds={selectedRowIds}
            actions={tableActions}
            toggleSelectRow={setSelectedRowIds}
          />

          <Formik
            enableReinitialize
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {() => (
              <Form>
                <StipRequestForm value={initialValues} />

                <CommonButtonWrapper>
                  <CancelButton
                    width="80px"
                    onClick={handleClose}
                  />
                  <SaveButton
                    label="Send Request"
                    width="115px"
                    disabled={selectedRowIds.length === 0}
                    isLoading={isSending}
                  />
                </CommonButtonWrapper>
              </Form>
            )}
          </Formik>
          {!!IsAssetRequiredModel && (
            <LeadNoAssetAssignedDataModal
              messageText="Selected Asset is not assigned to a lead yet. Please open the leads tab and assign the asset to a lead before requesting stips."
              handleClose={() => setIsAssetRequiredModel(0)}
            />
          )}
        </ModalWrapper>
      )}
    </Modal>
  );
};


